import { type AvatarProps, Stack, Text } from '@chakra-ui/react'
import React, { type ReactElement, useCallback } from 'react'

import { useUser } from '@/client/context/user'
import { RPCRefresh } from '@/client/rpc'

import MultipleUserAvatar from '@/components/MultipleUserAvatar'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'
import UserAvatar from '@/components/UserAvatar'
import AnniversaryIcon from '@/components/Worlds/UI/Anniversary/AnniversaryIcon'

import type { HostToastInfo } from './CohostReviewModal/types'
import HostTable from './HostTable'
import type { HostAvatarDetails } from './types'

const NAME_TRUNCATION_LENGTH = 25

export default function HostAvatars({
  hosts,
  numHosts,
  isSession,
  isTooltip,
  size = 'md',
}: {
  hosts: HostAvatarDetails[]
  isTooltip: boolean
  isSession: boolean
  numHosts: number
  size?: AvatarProps['size']
}): ReactElement {
  const toast = useToaster()
  const currentUser = useUser()
  const [removeCohostInfo, setRemoveCohostInfo] = React.useState<HostToastInfo | null>(null)
  const [showTableModal, setShowTableModal] = React.useState(false)

  const mainHost = hosts.find(host => host.role === 'main')
  const mainHostAvatarSrc = mainHost?.avatarSrc
  const mainHostName = mainHost?.avatarName
  const mainHostProgressionLevel = mainHost?.progressionLevel ?? null
  const mainHostIsCurrentUser = mainHost?.userId === currentUser.id
  const mainHostIsUserText = mainHostIsCurrentUser ? ' (you)' : ''
  const mainHostActiveAnniversary = mainHost?.activeAnniversary ?? null

  const namesText =
    numHosts === 1
      ? `${mainHostName ?? ''}${mainHostIsUserText}`
      : numHosts === 2
        ? `${mainHostName ?? ''}${mainHostIsUserText} and ${hosts[1].avatarName ?? ''}${
            hosts[1].userId === currentUser.id ? ' (you)' : ''
          }`
        : `${mainHostName ?? ''}${mainHostIsUserText} and ${numHosts - 1} others`

  const singleAvatar = (
    <UserAvatar
      src={`${mainHostAvatarSrc}`}
      name={`${mainHostName}}`}
      progressionLevel={mainHostProgressionLevel}
      size={size}
    />
  )

  const removeFormInputs: FormInputType[] = React.useMemo(
    () => [
      {
        label: 'Write a message (optional)',
        type: 'textarea',
        name: 'reason',
        placeholder: `Let ${removeCohostInfo?.name} know why you are removing them`,
        required: false,
        size: 6,
      },
    ],
    [removeCohostInfo]
  )
  const handleRemove = useCallback(
    async (values: FormValues) => {
      if (removeCohostInfo == null) {
        return
      }
      if (isSession) {
        await RPCRefresh.removeSessionHost({
          sessionHostId: removeCohostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      } else {
        await RPCRefresh.removeSeriesHost({
          seriesHostId: removeCohostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      }
      toast({
        title: `You removed ${removeCohostInfo.name} as a co-host.`,
        status: 'success',
      })
      setRemoveCohostInfo(null)
    },
    [toast, removeCohostInfo, isSession]
  )

  if (isTooltip) {
    return (
      <Tippy content={hosts.map(host => host.avatarName).join(', ')}>
        <Stack direction="row" spacing="8px" align="center">
          {numHosts === 1 ? singleAvatar : <MultipleUserAvatar hosts={hosts} size="sm" />}
          <Text fontWeight="bold">
            {namesText.slice(0, NAME_TRUNCATION_LENGTH)}
            {namesText.length > NAME_TRUNCATION_LENGTH ? '...' : ''}
          </Text>
        </Stack>
      </Tippy>
    )
  }

  return (
    <>
      <Stack
        direction="row"
        spacing="8px"
        align="center"
        borderRadius="8px"
        p="8px"
        _hover={{ backgroundColor: 'bg.muted' }}
        cursor="pointer"
        onClick={() => setShowTableModal(true)}
        flex={1}
      >
        {numHosts === 1 ? singleAvatar : <MultipleUserAvatar hosts={hosts} size="md" />}
        <Text fontWeight="bold" noOfLines={1}>
          {namesText}
        </Text>
        {numHosts === 1 && mainHostActiveAnniversary != null ? (
          <AnniversaryIcon
            years={mainHostActiveAnniversary}
            isCurrentUser={mainHostIsCurrentUser}
            displayName={mainHostName ?? ''}
            includeTooltip
          />
        ) : null}
      </Stack>
      <ModalV3
        open={showTableModal}
        onClose={() => {
          setShowTableModal(false)
        }}
        title="Hosts"
        size="xl"
        body={() => (
          <HostTable
            hosts={hosts}
            canRemoveHosts={currentUser.id === mainHost?.userId}
            setRemoveCohostInfo={setRemoveCohostInfo}
            setShowTableModal={setShowTableModal}
          />
        )}
      />
      <ModalV3
        title="Remove Co-host"
        size="xl"
        open={removeCohostInfo != null}
        onClose={() => {
          setRemoveCohostInfo(null)
          setShowTableModal(true)
        }}
        body={() => (
          <>
            <Text>
              Are you sure you want to remove {removeCohostInfo?.name} as a co-host? They will need
              to request again in order to become a co-host again.
            </Text>
            <Form name="CohostRemove" blocks={removeFormInputs} onSubmit={handleRemove} />
          </>
        )}
      />
    </>
  )
}
